import { timestamps, uuid } from '@snap-types/models/common';
import {
  FeatureVisibility,
  featureVisibilitySchema,
  leadTimeFormatSchema,
  partSpecTableSchema,
  SellingUnitOfMeasure,
  sellingUnitOfMeasureSchema,
  wonLossStatusSchema,
} from '@snap-types/models/enums';
import { schema } from '@snap-types/schemas';
import { decimal } from '@snap-types/schemas/decimal';
import { dec } from '@snap-types/types/decimal';
import { z } from 'zod/v4';

export const columns = {
  uuid,
  active: z.boolean(),

  useConfigurator: z.boolean(),
  useImportLineItems: z.boolean(),
  consolidateImportedLineItems: z.boolean(),
  useQuoteWonLossStatus: z.boolean(),

  defaultPartRequirementFixedCost: z.number().int().nullish(),
  sellingUnitOfMeasure: sellingUnitOfMeasureSchema,
  decimalPlaces: z.number(),
  defaultWonLossStatus: wonLossStatusSchema,
  partSpecTable: partSpecTableSchema,
  leadTimeFormat: leadTimeFormatSchema,
  productionDaysPerWeek: z.number().int(),
  transitDaysPerWeek: z.number().int(),
  planningDaysPerWeek: z.number().int(),
  enableUnsupportedCalculationsFlag: z.boolean().default(false),
  showCostFieldsForFixedPriceStrategies: z.boolean().default(false),

  partInsightsMaxDaysForInclusion: z.number(),
  partInsightsMaxDaysForRecent: z.number(),
  partInsightsHighWinRate: decimal,
  partInsightsLowWinRate: decimal,
  partInsightsLastOrderGrossMarginThreshold: decimal,
  partInsightsLeadTimeRangeLow: decimal,
  partInsightsLeadTimeRangeHigh: decimal,
  partInsightsLeadTimeMaxDaysForInclusion: z.number(),

  // Part Search customization
  partSearchVisible: featureVisibilitySchema,
  partSearchField_user1_enabled: z.boolean().default(false),
  partSearchField_user1_name: z.string().default(''),
  partSearchField_user2_enabled: z.boolean().default(false),
  partSearchField_user2_name: z.string().default(''),
  partSearchField_user3_enabled: z.boolean().default(false),
  partSearchField_user3_name: z.string().default(''),
  partSearchField_user4_enabled: z.boolean().default(false),
  partSearchField_user4_name: z.string().default(''),
  partSearchField_user5_enabled: z.boolean().default(false),
  partSearchField_user5_name: z.string().default(''),
  partSearchField_user6_enabled: z.boolean().default(false),
  partSearchField_user6_name: z.string().default(''),
  partSearchField_user7_enabled: z.boolean().default(false),
  partSearchField_user7_name: z.string().default(''),
  partSearchField_user8_enabled: z.boolean().default(false),
  partSearchField_user8_name: z.string().default(''),
  partSearchField_user9_enabled: z.boolean().default(false),
  partSearchField_user9_name: z.string().default(''),
  partSearchField_user10_enabled: z.boolean().default(false),
  partSearchField_user10_name: z.string().default(''),
  partSearchField_productCode_enabled: z.boolean().default(false),
  partSearchField_commodityCode_enabled: z.boolean().default(false),

  partSearchField_inventory_enabled: z.boolean().default(true),
  partSearchField_customerPartId_enabled: z.boolean().default(true),

  // Feature Visibility
  partInsightsCrossSitePartIdVisible: featureVisibilitySchema,
  partInsightsPartHistoryVisible: featureVisibilitySchema,
  partInsightsLeadTimeVisible: featureVisibilitySchema,
  partInsightsLastOrderGrossMarginVisible: featureVisibilitySchema,
  duplicateQuoteVisible: featureVisibilitySchema,
  searchExistingCustomerPartIdsVisible: featureVisibilitySchema,
  alphaVisible: featureVisibilitySchema,

  // Import File Upload Settings
  sendImportFilesToVisual: z.boolean(),
  importFileSizeLimitBytes: z.number().int(),

  createdByUserUuid: z.uuid().nullish(),
  deletedAt: z.date().nullish(),
  ...timestamps,
} as const;

const d = schema.t.dec;

/**
 * A site's week length, for one of the several kinds of waiting a lead time is
 * built from. Each kind is recorded on its own week — the shop floor on the
 * working week, transit and planning lead times on whatever week the site keeps
 * them in — so they get a validator each rather than sharing one setting.
 */
const daysPerWeek = schema.t.num.fromString
  .pipe(
    schema.t.int.positive
      .min(1, { error: 'Must be 1 or more' })
      .max(7, { error: 'Must be 7 or less' }),
  )
  .default(7);

export const columnValidators = {
  active: z.boolean(),

  useConfigurator: z.boolean(),
  useImportLineItems: z.boolean(),
  consolidateImportedLineItems: z.boolean(),
  useQuoteWonLossStatus: z.boolean(),

  defaultPartRequirementFixedCost: schema.t.num.fromString
    .pipe(schema.t.int.positive)
    .nullish()
    .or(z.literal('').transform(() => null)),
  sellingUnitOfMeasure: sellingUnitOfMeasureSchema.default(
    SellingUnitOfMeasure.EA,
  ),
  decimalPlaces: schema.t.num.fromString
    .pipe(
      schema.t.int.positive
        .min(1, { error: 'Must be 1 or more' })
        .max(10, { error: 'Must be 10 or less' }),
    )
    .default(2),
  defaultWonLossStatus: wonLossStatusSchema,
  partSpecTable: partSpecTableSchema,
  leadTimeFormat: leadTimeFormatSchema,
  // Mirrors the database CHECK constraint. Zero would divide by zero in the
  // days <-> weeks conversions and more than seven is not a week.
  productionDaysPerWeek: daysPerWeek,
  transitDaysPerWeek: daysPerWeek,
  planningDaysPerWeek: daysPerWeek,
  partInsightsMaxDaysForInclusion: schema.t.num.fromString
    .pipe(schema.t.int.positive)
    .default(1095),
  partInsightsMaxDaysForRecent: schema.t.num.fromString
    .pipe(schema.t.int.positive)
    .default(365),
  partInsightsHighWinRate: d.fromDecimalLike
    .default(dec(0.7))
    .check(d.checks.gte(0))
    .check(d.checks.lte(dec(0.99))),
  partInsightsLowWinRate: d.fromDecimalLike
    .default(dec(0.3))
    .check(d.checks.gte(0))
    .check(d.checks.lte(dec(0.99))),
  partInsightsLastOrderGrossMarginThreshold: d.fromDecimalLike
    .default(dec(0.3))
    .check(d.checks.lte(dec(0.99))),
  partInsightsCrossSitePartIdVisible: featureVisibilitySchema.default(
    FeatureVisibility.ALL,
  ),
  partInsightsPartHistoryVisible: featureVisibilitySchema.default(
    FeatureVisibility.ALL,
  ),
  partInsightsLeadTimeVisible: featureVisibilitySchema.default(
    FeatureVisibility.ALL,
  ),
  partInsightsLastOrderGrossMarginVisible: featureVisibilitySchema.default(
    FeatureVisibility.NONE,
  ),
  partInsightsLeadTimeRangeLow: d.fromDecimalLike
    .default(dec(0.1))
    .check(d.checks.gt(dec(0)))
    .check(d.checks.lt(dec(9.995))),
  partInsightsLeadTimeRangeHigh: d.fromDecimalLike
    .default(dec(0.3))
    .check(d.checks.gt(dec(0)))
    .check(d.checks.lt(dec(9.995))),
  partInsightsLeadTimeMaxDaysForInclusion: schema.t.num.fromString
    .pipe(schema.t.int.positive)
    .default(730),
  duplicateQuoteVisible: featureVisibilitySchema.default(FeatureVisibility.ALL),
  searchExistingCustomerPartIdsVisible: featureVisibilitySchema.default(
    FeatureVisibility.NONE,
  ),
  alphaVisible: featureVisibilitySchema.default(FeatureVisibility.NONE),
  sendImportFilesToVisual: z.boolean().default(true),
  importFileSizeLimitBytes: schema.t.num.fromString
    .pipe(schema.t.int.positive)
    // Max ~2GB (PostgreSQL INT4 max is 2,147,483,647 bytes)
    .pipe(
      z.int32().min(1).max(2047, { error: 'Must be between 1 and 2047 MB' }),
    )
    .default(10),

  enableUnsupportedCalculationsFlag: z.boolean().default(false),
  showCostFieldsForFixedPriceStrategies: z.boolean().default(false),

  partSearchVisible: featureVisibilitySchema.default(FeatureVisibility.NONE),
  partSearchField_user1_enabled: z.boolean().default(false),
  partSearchField_user1_name: z.string().default(''),
  partSearchField_user2_enabled: z.boolean().default(false),
  partSearchField_user2_name: z.string().default(''),
  partSearchField_user3_enabled: z.boolean().default(false),
  partSearchField_user3_name: z.string().default(''),
  partSearchField_user4_enabled: z.boolean().default(false),
  partSearchField_user4_name: z.string().default(''),
  partSearchField_user5_enabled: z.boolean().default(false),
  partSearchField_user5_name: z.string().default(''),
  partSearchField_user6_enabled: z.boolean().default(false),
  partSearchField_user6_name: z.string().default(''),
  partSearchField_user7_enabled: z.boolean().default(false),
  partSearchField_user7_name: z.string().default(''),
  partSearchField_user8_enabled: z.boolean().default(false),
  partSearchField_user8_name: z.string().default(''),
  partSearchField_user9_enabled: z.boolean().default(false),
  partSearchField_user9_name: z.string().default(''),
  partSearchField_user10_enabled: z.boolean().default(false),
  partSearchField_user10_name: z.string().default(''),
  partSearchField_productCode_enabled: z.boolean().default(false),
  partSearchField_commodityCode_enabled: z.boolean().default(false),

  partSearchField_inventory_enabled: z.boolean().default(true),
  partSearchField_customerPartId_enabled: z.boolean().default(true),
} as const;
